import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { Modal, ModalProps, useModal } from 'features/modals';

import history from '../../routing/history';

export const useUnsavedChangesPrompt = ({
  isDirty,
  onSave,
  isPublished,
}: {
  isDirty: boolean;
  onSave(): Promise<void>;
  isPublished: boolean;
}) => {
  const { showModal } = useModal();
  const navigate = useNavigate();
  useEffect(() => {
    if (isDirty) {
      const unblock = history.block((tx) => {
        const state = tx.location.state;
        if (state === 'override') {
          unblock();
          navigate(tx.location.pathname);
        } else {
          showModal('JOB_UNSAVED_CHANGES', {
            _container: {
              closeOnOverlayClickOrEsc: false,
              autoWidth: true,
              minWidth: '500px',
            },
            onSave: async () => {
              await onSave();
            },
            onDiscard: () => {
              unblock();
              tx.retry();
            },
            path: tx.location.pathname,
            isPublished,
          });
        }
      });
      return unblock;
    }
  }, [isDirty, onSave, showModal, isPublished, navigate]);
};

type Props = ModalProps<{
  onSave: () => Promise<void>;
  onDiscard: () => void;
  path: string;
  isPublished: boolean;
}>;

export const UnsavedChangesModal = (props: Props) => {
  const { closeModal, onSave, onDiscard, path, isPublished } = props;
  const { t } = useScopedTranslate(UnsavedChangesLocale);
  const navigate = useNavigate();

  return (
    <Modal>
      <Stack space={4}>
        <Stack orientation="horizontal" space={2} justify="space-between">
          <Text font="heading1" text={t('title')} />
          <IconButton variant="ghost-purple" onClick={closeModal} icon={<IconCloseRegular />} />
        </Stack>
        <Stack space={4}>
          <Text text={isPublished ? t('PublishedDescription') : t('description')} />
        </Stack>
        <Stack orientation="horizontal" justify="space-around">
          <Modal.Actions>
            <Button variant="secondary" onClick={closeModal} text={t('cancel')} />
            <Button
              variant="primary-red"
              onClick={() => {
                onDiscard();
                navigate(path);
                closeModal();
              }}
              text={t('discardClose')}
            />
            <Button
              variant="primary-blue"
              onClick={async () => {
                await onSave();
                closeModal();
                navigate(path, { state: 'override' });
              }}
              text={isPublished ? t('publishClose') : t('saveClose')}
            />
          </Modal.Actions>
        </Stack>
      </Stack>
    </Modal>
  );
};

const UnsavedChangesLocale = {
  en: {
    title: 'Unsaved changes',
    description: 'You have unsaved changes.\nWould you like to save or discard them? ',
    PublishedDescription: 'You have unsaved changes.\nWould you like to publish or discard them? ',
    cancel: 'Cancel',
    discardClose: 'Discard & Continue',
    saveClose: 'Save & Continue',
    publishClose: 'Publish changes & Continue',
  },
  is: {
    title: 'Óvistaðar breytingar',
    description: 'Þú hefur óvistaðar breytingar.\nViltu vista eða henda þeim? ',
    PublishedDescription: 'Þú hefur óvistaðar breytingar.\nViltu birta þær eða henda? ',
    cancel: 'Hætta við',
    discardClose: 'Henda & halda áfram',
    saveClose: 'Vista & halda áfram',
    publishClose: 'Birta breytingar & Halda áfram',
  },
};
